import { useEffect, useRef, useState } from "react";

import { useLanguage } from "../i18n";
import type { LastSoil } from "../tasksApi";

const SOIL: Array<{ value: string; key: "soilWet" | "soilMoist" | "soilDry" | "soilVeryDry" }> = [
  { value: "wet", key: "soilWet" },
  { value: "moist", key: "soilMoist" },
  { value: "dry", key: "soilDry" },
  { value: "very_dry", key: "soilVeryDry" },
];

const READING_KEYS = {
  wet: "soilWet",
  moist: "soilMoist",
  dry: "soilDry",
  very_dry: "soilVeryDry",
} as const;

/**
 * The soil bar, and its absence.
 *
 * One track, not four buttons: it is an ordered axis from wet to dry, and the
 * tap lands anywhere inside a quarter. The chosen quarter lifts out of the
 * track, and that lift is the confirmation.
 *
 * It is an *ask*, though — the guide says when to look, and these four quarters
 * are the app putting that question to you. On a row where no Check is due the
 * question is not being asked, so the bar folds down to a single strip carrying
 * the reason for the silence: what the soil last felt like, and when. Tapping
 * the strip unfolds it, because poking a plant the app did not ask about must
 * still be one tap from the list.
 *
 * It cannot simply be drawn shorter. Every control here meets a 48px floor, so
 * four quarters at 48px would save almost nothing against the 64px they use.
 * The saving is one target instead of four.
 */
export function SoilBar({
  found,
  lastSoil,
  askedFor,
  onChoose,
}: {
  /** The reading already logged in this session, if any. */
  found?: string;
  /** What the app last learned about this pot. */
  lastSoil?: LastSoil;
  /** Whether a Check is actually due. When it is not, the bar starts folded. */
  askedFor: boolean;
  onChoose: (value: string, label: string) => void;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const justOpened = useRef(false);

  // Folding back once a reading is given: the strip then carries what was just
  // said. Nothing about the open state survives a reload — a row left open for
  // reasons set weeks ago is a row you cannot explain.
  useEffect(() => {
    if (found) setOpen(false);
  }, [found]);

  const expanded = askedFor || open;

  if (!expanded) {
    return (
      <button
        type="button"
        className="soil-strip"
        aria-expanded={false}
        onClick={() => {
          justOpened.current = true;
          setOpen(true);
        }}
      >
        <span className="soil-strip-text">{memoryText(found, lastSoil, t)}</span>
        <span className="soil-strip-open" aria-hidden="true">
          {t.checkTheSoil}
        </span>
      </button>
    );
  }

  return (
    <div
      className="soil-bar"
      role="group"
      aria-label={t.checkSoil}
      data-unfolded={!askedFor || undefined}
    >
      <span className="soil-end wet" aria-hidden="true">
        {t.soilEndWet}
      </span>
      <span className="soil-end dry" aria-hidden="true">
        {t.soilEndDry}
      </span>
      <div className="soil-track">
        {SOIL.map((soil) => (
          <button
            key={soil.value}
            type="button"
            className="soil-step"
            aria-pressed={found === soil.value}
            onClick={() => onChoose(soil.value, t[soil.key])}
          >
            <span>{t[soil.key]}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * The reason the app is not asking.
 *
 * Deliberately a fact and not a conclusion. It does not say the plant needs
 * nothing: the app not asking is not the same as the plant not needing, and
 * putting that in the guide's mouth would be inventing horticulture.
 */
function memoryText(
  found: string | undefined,
  lastSoil: LastSoil | undefined,
  t: ReturnType<typeof useLanguage>["t"],
): string {
  // A reading given moments ago has not reached the plan yet — nothing is
  // written until the grace window closes. The strip says what you just told
  // it rather than the fact it is about to replace.
  if (found && found in READING_KEYS) {
    return t.soilJustFelt(t[READING_KEYS[found as keyof typeof READING_KEYS]]);
  }
  if (!lastSoil) return t.nothingLogged;
  if (lastSoil.kind === "water" || !lastSoil.reading) {
    return t.soilWateredNotFelt(lastSoil.days_ago);
  }
  return t.soilLastFelt(t[READING_KEYS[lastSoil.reading]], lastSoil.days_ago);
}
